import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import ForgotPassword from './pages/Auth/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';
import DashboardLayout from './components/Layout/DashboardLayout';
import MemberLayout from './components/Layout/MemberLayout'; // ✅ new layout
import Overview from './pages/Overview';
import RegistrationPayment from './pages/Auth/RegistrationPayment';

// Super Admin pages
import SuperAdminDashboard from './pages/SuperAdmin/Dashboard';
import OrganizationList from './pages/SuperAdmin/Organizations/OrganizationList';
import OrganizationForm from './pages/SuperAdmin/Organizations/OrganizationForm';
import OrganizationDetail from './pages/SuperAdmin/Organizations/OrganizationDetail';
import SuperAdminUsers from './pages/SuperAdmin/Users/SuperAdminUsers';

// Org Admin pages
import OrgAdminDashboard from './pages/OrgAdmin/Dashboard';
import AdminUnitTree from './pages/OrgAdmin/AdminUnits/AdminUnitTree';
import UserList from './pages/OrgAdmin/Users/UserList';
import UserForm from './pages/OrgAdmin/Users/UserForm';
import UserDetail from './pages/OrgAdmin/Users/UserDetail';
import MemberList from './pages/OrgAdmin/Members/MemberList';
import MemberDetail from './pages/OrgAdmin/Members/MemberDetail';
import MemberApproval from './pages/OrgAdmin/Members/MemberApproval';
import BeneficiaryForm from './pages/OrgAdmin/Members/BeneficiaryForm';
import CaseList from './pages/OrgAdmin/Cases/CaseList';
import CaseForm from './pages/OrgAdmin/Cases/CaseForm';
import CaseDetail from './pages/OrgAdmin/Cases/CaseDetail';
import CaseApproval from './pages/OrgAdmin/Cases/CaseApproval';
import ContributionReport from './pages/OrgAdmin/Contributions/ContributionReport';
import GeneralReports from './pages/OrgAdmin/Reports/GeneralReports';
import AuditLogs from './pages/OrgAdmin/Reports/AuditLogs';
import SystemSettings from './pages/OrgAdmin/Settings/SystemSettings';
import IntegrationSettings from './pages/OrgAdmin/Settings/IntegrationSettings';
import Profile from './pages/OrgAdmin/Profile';

// Unit Admin pages
import UnitAdminDashboard from './pages/UnitAdmin/Dashboard';
import UnitMembers from './pages/UnitAdmin/Members';
import UnitCases from './pages/UnitAdmin/Cases';
import UnitReports from './pages/UnitAdmin/Reports';

// Member pages
import CreateCase from './pages/Member/CreateCase';
import MemberBeneficiaries from './pages/Member/Beneficiaries';

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" />;
  if (!allowedRoles.includes(user.role)) return <Navigate to="/dashboard" />;
  return children;
};

function AppRoutes() {
  const { user } = useAuth();
  const role = user?.role;

  const getDefaultPath = () => {
    if (role === 'super_admin') return '/dashboard/super-admin';
    if (role === 'org_admin') return '/dashboard/org-admin';
    if (role === 'admin_unit_admin') return '/dashboard/unit-admin';
    return '/login';
  };

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/payment/register/:memberId" element={<RegistrationPayment />} />

      {/* Admin routes with sidebar */}
      <Route path="/dashboard" element={<DashboardLayout />}>
        <Route index element={<Overview />} />

        {/* Super Admin */}
        <Route path="super-admin" element={<ProtectedRoute allowedRoles={['super_admin']}><SuperAdminDashboard /></ProtectedRoute>} />
        <Route path="super-admin/organizations" element={<ProtectedRoute allowedRoles={['super_admin']}><OrganizationList /></ProtectedRoute>} />
        <Route path="super-admin/organizations/new" element={<ProtectedRoute allowedRoles={['super_admin']}><OrganizationForm /></ProtectedRoute>} />
        <Route path="super-admin/organizations/:id" element={<ProtectedRoute allowedRoles={['super_admin']}><OrganizationForm /></ProtectedRoute>} />
        <Route path="super-admin/organizations/:id/detail" element={<ProtectedRoute allowedRoles={['super_admin']}><OrganizationDetail /></ProtectedRoute>} />
        <Route path="super-admin/users" element={<ProtectedRoute allowedRoles={['super_admin']}><SuperAdminUsers /></ProtectedRoute>} />
        <Route path="super-admin/users/new" element={<ProtectedRoute allowedRoles={['super_admin']}><UserForm /></ProtectedRoute>} />
        <Route path="super-admin/users/:id" element={<ProtectedRoute allowedRoles={['super_admin']}><UserDetail /></ProtectedRoute>} />

        {/* Org Admin */}
        <Route path="org-admin" element={<ProtectedRoute allowedRoles={['org_admin']}><OrgAdminDashboard /></ProtectedRoute>} />
        <Route path="org-admin/admin-units" element={<ProtectedRoute allowedRoles={['org_admin']}><AdminUnitTree /></ProtectedRoute>} />
        <Route path="org-admin/users" element={<ProtectedRoute allowedRoles={['org_admin']}><UserList /></ProtectedRoute>} />
        <Route path="org-admin/users/new" element={<ProtectedRoute allowedRoles={['org_admin']}><UserForm /></ProtectedRoute>} />
        <Route path="org-admin/users/:id" element={<ProtectedRoute allowedRoles={['org_admin']}><UserDetail /></ProtectedRoute>} />
        <Route path="org-admin/members" element={<ProtectedRoute allowedRoles={['org_admin']}><MemberList /></ProtectedRoute>} />
        <Route path="org-admin/members/:id" element={<ProtectedRoute allowedRoles={['org_admin']}><MemberDetail /></ProtectedRoute>} />
        <Route path="org-admin/members/:id/approve" element={<ProtectedRoute allowedRoles={['org_admin']}><MemberApproval /></ProtectedRoute>} />
        <Route path="org-admin/members/:id/beneficiaries" element={<ProtectedRoute allowedRoles={['org_admin']}><BeneficiaryForm /></ProtectedRoute>} />
        <Route path="org-admin/cases" element={<ProtectedRoute allowedRoles={['org_admin']}><CaseList /></ProtectedRoute>} />
        <Route path="org-admin/cases/new" element={<ProtectedRoute allowedRoles={['org_admin']}><CaseForm /></ProtectedRoute>} />
        <Route path="org-admin/cases/:id" element={<ProtectedRoute allowedRoles={['org_admin']}><CaseDetail /></ProtectedRoute>} />
        <Route path="org-admin/cases/:id/approve" element={<ProtectedRoute allowedRoles={['org_admin']}><CaseApproval /></ProtectedRoute>} />
        <Route path="org-admin/contributions" element={<ProtectedRoute allowedRoles={['org_admin']}><ContributionReport /></ProtectedRoute>} />
        <Route path="org-admin/reports" element={<ProtectedRoute allowedRoles={['org_admin']}><GeneralReports /></ProtectedRoute>} />
        <Route path="org-admin/audit" element={<ProtectedRoute allowedRoles={['org_admin']}><AuditLogs /></ProtectedRoute>} />
        <Route path="org-admin/settings" element={<ProtectedRoute allowedRoles={['org_admin']}><SystemSettings /></ProtectedRoute>} />
        <Route path="org-admin/settings/integrations" element={<ProtectedRoute allowedRoles={['org_admin']}><IntegrationSettings /></ProtectedRoute>} />
        <Route path="org-admin/profile" element={<ProtectedRoute allowedRoles={['org_admin']}><Profile /></ProtectedRoute>} />

        {/* Unit Admin */}
        <Route path="unit-admin" element={<ProtectedRoute allowedRoles={['admin_unit_admin']}><UnitAdminDashboard /></ProtectedRoute>} />
        <Route path="unit-admin/members" element={<ProtectedRoute allowedRoles={['admin_unit_admin']}><UnitMembers /></ProtectedRoute>} />
        <Route path="unit-admin/cases" element={<ProtectedRoute allowedRoles={['admin_unit_admin']}><UnitCases /></ProtectedRoute>} />
        <Route path="unit-admin/reports" element={<ProtectedRoute allowedRoles={['admin_unit_admin']}><UnitReports /></ProtectedRoute>} />
      </Route>

      {/* Member routes – no sidebar */}
      <Route path="/dashboard/member" element={<MemberLayout />}>
        <Route index element={<Overview />} />
        <Route path="cases/new" element={<CreateCase />} />
        <Route path="beneficiaries" element={<MemberBeneficiaries />} />
        {/* Add more member routes here */}
      </Route>

      {/* Fallback */}
      <Route path="/" element={<Navigate to={getDefaultPath()} />} />
      <Route path="*" element={<div>404 Not Found</div>} />
    </Routes>
  );
}

export default AppRoutes;